import { buildBodyRootMap, findBodyForJointUuid } from "../../utils/bodyHierarchy";
import { resolveJointUuidFromRef } from "../../utils/jointUtils";
import {
  generateActuatorControlName,
  generateStableActuatorNameFromUuid,
} from "../../utils/actuatorIds";
import type { Actuator, BodyRecord, JointRecord, MappedJoint, WorldBodyEntry } from "./types";

const clampActuatorInspector = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function positionLikeActuatorKind(kind: string | undefined): boolean {
  const t = String(kind || "").toLowerCase();
  return t === "position" || t === "intposition" || t === "general";
}

/**
 * Physics UI control key aligned with {@link ActuatorControlsPanel} (stable UUID when present,
 * otherwise resolved joint + naming rules).
 */
export function resolveInspectorPositionActuatorControlKey(
  actuator: Actuator,
  sceneState: {
    joints?: JointRecord;
    bodies?: BodyRecord;
    worldBodies?: WorldBodyEntry[];
  }
): string | null {
  if (!actuator) return null;
  const joints = sceneState?.joints || {};
  const bodies = sceneState?.bodies || {};
  const worldBodies = Array.isArray(sceneState?.worldBodies) ? sceneState.worldBodies : [];

  if (actuator.uuid) {
    return generateStableActuatorNameFromUuid(String(actuator.uuid));
  }

  const jointRef = actuator.joint;
  const jointUuid =
    jointRef && typeof jointRef === "string" ? resolveJointUuidFromRef(jointRef, joints) : null;

  const hostBodyUuid = jointUuid ? findBodyForJointUuid(bodies, jointUuid) : null;
  const bodyRootMap = buildBodyRootMap(bodies || {}, worldBodies);
  const rootUuid = hostBodyUuid ? bodyRootMap[hostBodyUuid] || hostBodyUuid : null;
  const jointEntry = jointUuid ? joints[jointUuid] : null;
  const baseName =
    (typeof actuator.name === "string" && actuator.name) ||
    jointEntry?.name ||
    (typeof actuator.tendon === "string" && actuator.tendon) ||
    "actuator";

  return generateActuatorControlName({
    baseName,
    jointUuid,
    rootBodyUuid: rootUuid || null,
    tendonName: typeof actuator.tendon === "string" ? actuator.tendon : null,
  });
}

export type ActuatorPositionInspectorSliderState = {
  controlKey: string;
  min: number;
  max: number;
  step: number;
  value: number;
  decimals: number;
};

/** Geometry for the inspector position slider, stable across joint `ref`-only edits. */
export type ActuatorPositionInspectorSliderLayout = {
  controlKey: string;
  min: number;
  max: number;
  step: number;
  decimals: number;
  tendon: boolean;
};

export function getActuatorPositionInspectorSliderLayout(
  actuator: Actuator | null | undefined,
  sceneState: {
    joints?: JointRecord;
    bodies?: BodyRecord;
    worldBodies?: WorldBodyEntry[];
    actuatorJointOverride?: MappedJoint | null;
  }
): ActuatorPositionInspectorSliderLayout | null {
  if (!actuator || !positionLikeActuatorKind(actuator.kind)) return null;

  const controlKey = resolveInspectorPositionActuatorControlKey(actuator, sceneState);
  if (!controlKey) return null;

  const joints = sceneState?.joints || {};
  const isTendon = Boolean(actuator.tendon);

  if (isTendon) {
    const lo = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[0] : 0;
    const hi = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[1] : 255;
    const min = Number.isFinite(lo) ? lo : 0;
    const max = Number.isFinite(hi) ? hi : 255;
    const step = (max - min) / 200 || 1;
    return { controlKey, min, max, step, decimals: 1, tendon: true };
  }

  let jointEntry: MappedJoint | null = null;
  if (actuator.joint) {
    const resolvedUuid =
      typeof actuator.joint === "string" ? resolveJointUuidFromRef(actuator.joint, joints) : null;
    jointEntry = resolvedUuid ? joints[resolvedUuid] : null;
  }
  if (!jointEntry && sceneState?.actuatorJointOverride) {
    jointEntry = sceneState.actuatorJointOverride;
  }

  const ctrlLo =
    Array.isArray(actuator.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[0]
      : null;
  const ctrlHi =
    Array.isArray(actuator.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[1]
      : null;
  const hasCtrlRange =
    Number.isFinite(ctrlLo) && Number.isFinite(ctrlHi) && (ctrlHi as number) > (ctrlLo as number);
  const ctrlLoNumber = hasCtrlRange ? (ctrlLo as number) : null;
  const ctrlHiNumber = hasCtrlRange ? (ctrlHi as number) : null;

  const jointLo = jointEntry?.range?.[0];
  const jointHi = jointEntry?.range?.[1];
  const hasJointRange =
    Number.isFinite(jointLo) &&
    Number.isFinite(jointHi) &&
    (jointHi as number) > (jointLo as number);
  const jointLoNumber = hasJointRange ? (jointLo as number) : null;
  const jointHiNumber = hasJointRange ? (jointHi as number) : null;

  const min =
    ctrlLoNumber !== null && jointLoNumber !== null
      ? Math.max(ctrlLoNumber, jointLoNumber)
      : ctrlLoNumber !== null
        ? ctrlLoNumber
        : jointLoNumber !== null
          ? jointLoNumber
          : -Math.PI;
  const max =
    ctrlHiNumber !== null && jointHiNumber !== null
      ? Math.min(ctrlHiNumber, jointHiNumber)
      : ctrlHiNumber !== null
        ? ctrlHiNumber
        : jointHiNumber !== null
          ? jointHiNumber
          : Math.PI;

  const step = (max - min) / 200 || 0.01;

  return {
    controlKey,
    min,
    max,
    step,
    decimals: 3,
    tendon: false,
  };
}

export function getActuatorInspectorSliderDisplayedValue(
  layout: ActuatorPositionInspectorSliderLayout,
  positionTargets: Record<string, number>,
  jointRefFallback: number
): number {
  const hasRange =
    Number.isFinite(layout.min) && Number.isFinite(layout.max) && layout.max > layout.min;
  const stored = positionTargets[layout.controlKey];

  if (layout.tendon) {
    const fallback = layout.min;
    const rawValue = Number.isFinite(Number(stored)) ? Number(stored) : fallback;
    return hasRange ? clampActuatorInspector(rawValue, layout.min, layout.max) : rawValue;
  }

  const fallback = Number.isFinite(jointRefFallback) ? jointRefFallback : 0;
  const rawValue = Number.isFinite(Number(stored)) ? Number(stored) : fallback;
  return hasRange ? clampActuatorInspector(rawValue, layout.min, layout.max) : rawValue;
}

/**
 * Slider geometry + store-backed value for position-like actuators while simulating
 * (matches {@link ActuatorControlsPanel} `renderPositionControl`).
 */
export function getActuatorPositionInspectorSliderState(
  actuator: Actuator | null | undefined,
  sceneState: {
    joints?: JointRecord;
    bodies?: BodyRecord;
    worldBodies?: WorldBodyEntry[];
  },
  positionTargets: Record<string, number>
): ActuatorPositionInspectorSliderState | null {
  const layout = getActuatorPositionInspectorSliderLayout(actuator, sceneState);
  if (!layout) return null;

  let jointRefFallback = 0;
  if (!layout.tendon && actuator?.joint && typeof actuator.joint === "string") {
    const joints = sceneState?.joints || {};
    const resolvedUuid = resolveJointUuidFromRef(actuator.joint, joints);
    const jointEntry = resolvedUuid ? joints[resolvedUuid] : null;
    const jointRef = jointEntry?.ref;
    jointRefFallback = Number.isFinite(Number(jointRef)) ? Number(jointRef) : 0;
  }

  const value = getActuatorInspectorSliderDisplayedValue(layout, positionTargets, jointRefFallback);
  const { tendon: _omit, ...rest } = layout;
  return { ...rest, value };
}
